import { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useRouter } from 'next/router'
import { isEmpty } from 'lodash-es'

import { format } from 'date-fns'

import { clearPreselectVender } from 'store/cache/cacheSlice'

import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

export const useDateTypeText = (services, serviceIds) => {
  const [dateTypeText, setDateTypeText] = useState('施作')

  useEffect(() => {
    if (!isEmpty(services)) {
      const selectedNonStandardService = services.some(
        (service) =>
          (serviceIds || []).includes(service._id) && !service.isStandard
      )
      setDateTypeText(selectedNonStandardService ? '估價' : '施作')
    }
  }, [services, serviceIds])

  return dateTypeText
}

const confirmModelInit = {
  open: false,
  onPositiveClick: () => {},
  onNegativeClick: () => {},
  positiveButtonText: null,
  negativeButtonText: null,
  title: '',
  message: '',
  // 這裡沒有宣告全部可用參數，有需要時再慢慢新增。
}

export const useConfirmModalHandler = () => {
  const [confirmModelConfig, setConfirmModelConfig] = useState(confirmModelInit)

  const close = () => {
    setConfirmModelConfig({
      ...confirmModelInit,
    })
  }

  const open = (config) => {
    setConfirmModelConfig({
      ...confirmModelInit,

      onNegativeClick: close,
      open: true,

      ...config,
    })
  }

  return [confirmModelConfig, open, close]
}

export const useAlbumModal = (pathname, router, options) => {
  const { webview, nativeHeaderHandler } = options

  const [albumModal, setAlbumModal] = useState({ open: false })
  const closeAlbumModal = () => setAlbumModal({ open: false })
  const onAlbumClick = (e, album) => {
    e.stopPropagation()
    e.preventDefault()
    if (!album) {
      throw new Error('useAlbumModal: album is undefined')
    }
    const albumUrlId = album.urlId.toLowerCase?.()
    const state = {
      ...window.history.state,
      url: `/album/${albumUrlId}`,
      as: `/album/${albumUrlId}`,
      idx: window.history.state.idx + 1,
    }
    const photo = album?.representativePhoto || album?.frontCover
    const photoUrlId = photo?.urlId
    const qs = window.location.href.split('?')?.[1] || ''
    const newQs = qs
      .split('&')
      .filter((string) => string && !/^(utm_|page=|tag=)/.test(string))
      .concat([`photoId=${photoUrlId}`])
      .join('&')
    window.history.pushState(state, '', `/album/${albumUrlId}?${newQs}`)
    router.query.albumUrlId = albumUrlId
    router.query.photoId = photoUrlId
    setAlbumModal({
      open: true,
      fallbackData: {
        photoUrlId: photoUrlId,
      },
    })
  }
  useEffect(() => {
    const handlePopState = () => {
      if (location.pathname === pathname && albumModal.open) {
        setAlbumModal({ open: false })
        if (webview?.name === 'lsApp' && nativeHeaderHandler) {
          nativeHeaderHandler()
        }
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [albumModal])
  const onAlbumContentClose = () => {
    router.back()
    closeAlbumModal()
    if (webview?.name === 'lsApp' && nativeHeaderHandler) {
      nativeHeaderHandler()
    }
  }

  return {
    albumModal,
    closeAlbumModal,
    onAlbumClick,
    onAlbumContentClose,
  }
}

export const usePreselectVenderManagement = () => {
  const router = useRouter()
  const dispatch = useDispatch()
  const [nextRoute, setNextRoute] = useState('')
  // 當跳轉到非下訂流程時，移除preselectVender
  useEffect(() => {
    const onRouteChangeStart = (url, { shallow }) => {
      if (shallow) {
        return
      }
      const qs = url.split('?')[1] || ''
      const hasPreselectVender = qs
        .split('&')
        .some((str) => /^preselect_vender=/.test(str))
      if (!hasPreselectVender) {
        return
      }
      if (!/^(\/service\/|\/login)/.test(url)) {
        const newQs = qs
          .split('&')
          .filter((str) => !/^preselect_vender=/.test(str))
          .join('&')
        dispatch(clearPreselectVender())
        setNextRoute(url.split('?')[0] + (newQs ? '?' + newQs : ''))
        router.events.emit('routeChangeError')
        throw 'route change aborted'
      }
    }

    if (nextRoute) {
      router.push(nextRoute)
    }

    router.events.on('routeChangeStart', onRouteChangeStart)

    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart)
    }
  }, [nextRoute])
}

export const useGetYearForFooter = () => {
  const [currentYear, setCurrentYear] = useState('')

  useEffect(() => {
    setCurrentYear(format(new Date(), 'yyyy'))
  }, [])

  return currentYear
}

export const useShowScrollToTopButton = () => {
  const theme = useTheme()
  const [showButton, setShowButton] = useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const showScrollButton = (e) => {
    if (isMobile) {
      e.currentTarget.scrollTop > 768
        ? setShowButton(true)
        : setShowButton(false)
    } else {
      e.currentTarget.scrollTop > 800
        ? setShowButton(true)
        : setShowButton(false)
    }
  }

  return {
    showButton,
    showScrollButton,
  }
}

export const useImageLoaded = () => {
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const ref = useRef()

  const onLoadComplete = () => {
    setIsImageLoaded(true)
  }

  useEffect(() => {
    if (ref.current && ref.current.complete) onLoadComplete()
  }, [])

  return [ref, isImageLoaded, onLoadComplete]
}
